import {
  DisplayService,
  type FormService,
  type Group,
  type IDisplayService,
  type Question,
  type Section,
} from '@stepform';

export class ExtractivesDisplayService extends DisplayService implements IDisplayService {
  constructor(formService: FormService) {
    super(formService);
  }

  public showQuestion(question: Question, group: Group): boolean {
    switch (question.id) {
      case 'PrimaryMaterial':
        return this.showPrimaryMaterial();
      case 'SubmitterRole':
        return this.showSubmitterRole();
      case 'CombinedNotifiableEvents':
        return this.hadNotifiableEvents();
      case 'CombinedFatalities':
        return this.hadFatalities();
      default:
        return true;
    }
  }

  public showGroup(group: Group): boolean {
    switch (group.id) {
      case 'submitter-details':
        return this.showSubmitterDetails();
      case 'manager-details':
        return this.showManagerDetails();
      case 'site-senior-executive-details':
        return this.showSSEDetails();
      case 'types-of-injuries-illnesses':
        return this.hadInjuriesOrIllness() && this.workersOnSite() && this.hadNotifiableEvents();
      default:
        return true;
    }
  }

  public showSection(section: Section): boolean {
    switch (section.id) {
      case 'section-workers-notifiable-events':
      case 'section-days-lost-alternative-duties':
        return this.workersOnSite();
      case 'section-notifiable-injuries-illnesses':
      case 'section-fatalities':
        return this.workersOnSite() && this.hadNotifiableEvents();
      default:
        return true;
    }
  }

  private showPrimaryMaterial(): boolean {
    const question = this.formService.getQuestionById('OperationType');
    const operationsWithMaterials = ['alluvial-mine', 'underground-mine', 'opencast-mine', 'quarry'];
    return question ? operationsWithMaterials.includes(String(question.answer)) : false;
  }

  private workersOnSite(): boolean {
    return this.getBooleanAnswer('WorkersOnSite');
  }

  private showSubmitterDetails(): boolean {
    if (!this.workersOnSite()) {
      return true;
    }

    const question = this.formService.getQuestionById('SubmitterRole');
    return question ? question.answer === 'other' : false;
  }

  private showManagerDetails(): boolean {
    return this.workersOnSite();
  }

  private showSSEDetails(): boolean {
    if (!this.workersOnSite()) {
      return false;
    }

    const question = this.formService.getQuestionById('OperationType');
    const operationsWithSSE = ['tunnel', 'underground-mine', 'opencast-mine', 'coal-exploration'];
    return question ? operationsWithSSE.includes(String(question.answer)) : false;
  }

  private showSubmitterRole(): boolean {
    return this.workersOnSite();
  }

  private hadNotifiableEvents(): boolean {
    return this.getBooleanAnswer('NotifiableEvents');
  }

  private hadInjuriesOrIllness(): boolean {
    return this.getBooleanAnswer('Injuries');
  }

  private hadFatalities(): boolean {
    return this.getBooleanAnswer('Fatalities');
  }

  private getBooleanAnswer(questionId: string): boolean {
    const question = this.formService.getQuestionById(questionId);
    return question ? question.answer === '1' : false;
  }
}
